import { useEffect, useState } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Flower2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocation } from "@/components/location-provider";
import {
  POLLEN_KEYS,
  POLLEN_LABELS,
  type PollenSnapshot,
  pollenLevel,
  pollenLevelColor,
  summarizePollen,
  type PollenLevel,
  type PollenKey,
} from "@/lib/insights/pollen";

const LEVEL_LABEL: Record<PollenLevel, string> = {
  none: "None",
  low: "Low",
  medium: "Moderate",
  high: "High",
  "very-high": "Very High",
};

export function PollenPanel() {
  const [data, setData] = useState<PollenSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { current: location } = useLocation();

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(`/api/pollen?lat=${location.lat}&lon=${location.lon}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [location.lat, location.lon]);

  if (loading) {
    return (
      <GlassPanel intensity="low" className="p-4">
        <div className="h-32 animate-pulse rounded-xl bg-muted/30" />
      </GlassPanel>
    );
  }

  if (error || !data || !data.available) {
    return (
      <GlassPanel intensity="low" className="p-4">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10">
            <Flower2 className="h-4 w-4 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight">Pollen</h3>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Allergens · Open-Meteo
            </p>
          </div>
        </div>
        <div className="rounded-lg bg-muted/20 border border-border/40 p-3 text-center">
          <p className="text-xs text-muted-foreground">
            Pollen forecasts are not available for this location. Coverage is best across
            Europe and parts of North America.
          </p>
        </div>
      </GlassPanel>
    );
  }

  const overall = summarizePollen(data.current);
  const overallColor = pollenLevelColor(overall.level);

  // Sort allergens by level so high ones surface first.
  const rank: Record<PollenLevel, number> = {
    none: 0,
    low: 1,
    medium: 2,
    high: 3,
    "very-high": 4,
  };
  const sorted = [...POLLEN_KEYS].sort((a, b) => {
    const la = pollenLevel(a, data.current[a]);
    const lb = pollenLevel(b, data.current[b]);
    return rank[lb] - rank[la];
  });

  return (
    <GlassPanel intensity="low" className="p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/20">
            <Flower2 className="h-4 w-4 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight">Pollen</h3>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Allergens · {data.units}
            </p>
          </div>
        </div>
        <div
          className={cn(
            "rounded-lg px-2.5 py-1 text-right ring-1 flex-shrink-0",
            overallColor.bg,
          )}
        >
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
            Overall
          </p>
          <p className={cn("text-sm font-bold", overallColor.text)}>
            {LEVEL_LABEL[overall.level]}
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        {sorted.map((key) => {
          const v = data.current[key];
          const lvl = pollenLevel(key as PollenKey, v);
          const c = pollenLevelColor(lvl);
          const meta = POLLEN_LABELS[key as PollenKey];
          const pct =
            v == null
              ? 0
              : lvl === "very-high"
                ? 100
                : Math.min(95, Math.max(8, (rank[lvl] / 4) * 100));
          return (
            <div
              key={key}
              className="flex items-center gap-2.5 rounded-lg bg-background/40 border border-border/40 px-2.5 py-1.5"
            >
              <span className="text-base flex-shrink-0" role="img">
                {meta.emoji}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-semibold">{meta.label}</span>
                  <span className={cn("text-[10px] font-bold tabular-nums", c.text)}>
                    {v == null ? "–" : v < 1 ? "<1" : v.toFixed(0)}
                    <span className="ml-1 text-muted-foreground font-normal">
                      · {LEVEL_LABEL[lvl]}
                    </span>
                  </span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-border/40 overflow-hidden">
                  <div
                    className={cn("h-full rounded-full transition-all", c.bar)}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {overall.topAllergen && (
        <p className="mt-3 text-[11px] text-muted-foreground">
          <span className="font-semibold text-foreground/80">Watch:</span>{" "}
          {POLLEN_LABELS[overall.topAllergen.key].label} is{" "}
          <span className={cn("font-semibold", pollenLevelColor(overall.topAllergen.level).text)}>
            {LEVEL_LABEL[overall.topAllergen.level]}
          </span>
          .
        </p>
      )}
    </GlassPanel>
  );
}
